// Iter40 — /sorting full workspace page
//
// Three sections:
//   1. My Sorting Tasks — open SRs across visible incidents
//   2. Sorting Results — flat results table with PDF/Excel/Import
//   3. Sorting Analytics — stats + Defect Pareto
// Header: "+ New Sorting Result" → SR picker → SortingResultForm inline.
//
// Module-scoped sorting users (module_access:['sorting']) can complete their
// entire workflow here without ever opening an incident panel.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useT } from "../lib/i18n";
import { useStore } from "../store";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";
import { DefectPareto } from "../components/DefectPareto";
import { SortingResultForm } from "../components/SortingResultForm";
import { CleanPointPanel } from "../components/CleanPointPanel";
import { SortingResultDetailDrawer } from "../components/SortingResultDetailDrawer";
import { NewSortingRequestModal } from "../components/NewSortingRequestModal";
import { UniversalFilter } from "../components/filters/UniversalFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const rateColor = (rate) => (rate > 10 ? "#E24B4A" : rate > 5 ? "#f3b14a" : "#4ed084");

const STATUS_FILTERS = [
  { key: "all", labelKey: "sorting.filter_all", fallback: "All" },
  { key: "pending", labelKey: "sorting.filter_pending", fallback: "Pending" },
  { key: "in_progress", labelKey: "sorting.filter_in_progress", fallback: "In Progress" },
  { key: "completed", labelKey: "sorting.filter_completed", fallback: "Completed" },
];

const STATUS_BADGE = {
  requested: { bg: "rgba(24,95,165,0.16)", fg: "#9ec3ec" },
  acknowledged: { bg: "rgba(155,89,182,0.16)", fg: "#c89adb" },
  in_progress: { bg: "rgba(243,177,74,0.16)", fg: "#f3b14a" },
  completed: { bg: "rgba(78,208,132,0.16)", fg: "#9aebc2" },
};

const PRIORITY_COLOR = { high: "#E24B4A", medium: "#f3b14a", low: "#9aa1b3" };

export default function Sorting() {
  const { t } = useT();
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);

  const canSubmit = hasModuleAccess(user, "sorting") &&
    ["responder", "escalation_manager", "tenant_admin", "platform_admin"].includes(user?.role);

  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, in_progress: 0, completed: 0 });
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [pareto, setPareto] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  // Inline form state — { sr_id, incident_id } when open.
  const [formFor, setFormFor] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Iter46 — standalone "create new sorting request" modal.
  const [newRequestOpen, setNewRequestOpen] = useState(false);

  // Auto-open the new-request modal when arriving from the Service Requests hub.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("new") === "1") setNewRequestOpen(true);
    } catch (_) { /* swallow */ }
  }, []);

  // Result detail drawer
  const [drawerId, setDrawerId] = useState(null);

  // Import flow state (Iter40)
  const [importSrPickerOpen, setImportSrPickerOpen] = useState(false);
  const [importTargetSr, setImportTargetSr] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const importInputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [tk, rs, st, pa] = await Promise.all([
        axios.get(`${API}/sorting/my-tasks?status=${filter}`),
        axios.get(`${API}/sorting/my-results?limit=200`),
        axios.get(`${API}/sorting/my-stats`),
        axios.get(`${API}/analytics/sorting-pareto`),
      ]);
      setTasks(tk.data.tasks || []);
      setCounts(tk.data.counts || {});
      setResults(Array.isArray(rs.data) ? rs.data : []);
      setStats(st.data);
      setPareto(pa.data);
    } catch (err) {
      addToast({ type: "error", title: "Failed to load sorting workspace", body: err.message });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [filter]);

  const openFormForSr = (sr) => {
    setPickerOpen(false);
    setFormFor({ srId: sr.id, incidentId: sr.incident_id, sr });
  };

  const onSubmitted = () => {
    setFormFor(null);
    addToast({ type: "success", title: "Sorting result submitted" });
    refresh();
  };

  const openSrsForPicker = useMemo(
    () => tasks.filter((t) => ["requested", "acknowledged", "in_progress"].includes(t.status)),
    [tasks],
  );

  // Iter51 — universal filter wiring for §1 tasks list + §2 results list.
  const taskChips = useMemo(() => [
    { key: "status_chip", label: "Status", type: "select", options: [
      { value: "requested", label: "Requested", predicate: (i) => i.status === "requested" },
      { value: "acknowledged", label: "Acknowledged", predicate: (i) => i.status === "acknowledged" },
      { value: "in_progress", label: "In progress", predicate: (i) => i.status === "in_progress" },
      { value: "completed", label: "Completed", predicate: (i) => i.status === "completed" },
    ]},
    { key: "priority_chip", label: "Priority", type: "select", options: [
      { value: "high", label: "High", predicate: (i) => (i.priority || "medium") === "high" },
      { value: "medium", label: "Medium", predicate: (i) => (i.priority || "medium") === "medium" },
      { value: "low", label: "Low", predicate: (i) => (i.priority || "medium") === "low" },
    ]},
    { key: "link_chip", label: "Link", type: "select", options: [
      { value: "linked", label: "Linked to incident", predicate: (i) => Boolean(i.incident_id) },
      { value: "standalone", label: "Standalone", predicate: (i) => !i.incident_id },
    ]},
    { key: "date_chip", label: "Created", type: "dateRange", field: "created_at" },
  ], []);
  const taskSearchText = useCallback((i) => [
    i.id, i.title, i.description, i.assignee_name, i.creator_name,
    i.incident_id, i.incident_title, i.tenant_id,
  ].filter(Boolean).join(" "), []);

  const resultChips = useMemo(() => [
    { key: "location_chip", label: "Location", type: "select", options: [
      { value: "on_site", label: "On site", predicate: (r) => r.sorting_location === "on_site" },
      { value: "facility", label: "Facility", predicate: (r) => r.sorting_location === "facility" },
      { value: "supplier", label: "Supplier", predicate: (r) => r.sorting_location === "supplier" },
    ]},
    { key: "rate_chip", label: "Defect rate", type: "select", options: [
      { value: "low", label: "≤ 5%", predicate: (r) => (r.overall_defect_rate_pct ?? r.defect_rate_pct ?? 0) <= 5 },
      { value: "mid", label: "5–10%", predicate: (r) => {
        const x = r.overall_defect_rate_pct ?? r.defect_rate_pct ?? 0;
        return x > 5 && x <= 10;
      }},
      { value: "high", label: "> 10%", predicate: (r) => (r.overall_defect_rate_pct ?? r.defect_rate_pct ?? 0) > 10 },
    ]},
    { key: "date_chip", label: "Date", type: "dateRange", field: "created_at" },
  ], []);
  const resultSearchText = useCallback((r) => {
    const entries = r.entries || [];
    const inspectors = entries.map((e) => e.inspector_name).filter(Boolean).join(" ");
    const batches = entries.map((e) => e.batch_number).filter(Boolean).join(" ");
    return [r.id, r.service_request_id, r.incident_id, r.incident_title, inspectors, batches, r.sorting_location].filter(Boolean).join(" ");
  }, []);

  return (
    <div data-testid="sorting-workspace">
      <Header
        t={t}
        canSubmit={canSubmit}
        onNewResult={() => setPickerOpen(true)}
        onNewRequest={() => setNewRequestOpen(true)}
      />

      {/* Iter46 — Standalone create modal (mirrors TMS pattern) */}
      {newRequestOpen && (
        <NewSortingRequestModal
          onClose={() => setNewRequestOpen(false)}
          onCreated={async () => {
            setNewRequestOpen(false);
            // Strip the ?new=1 marker so navigation refresh doesn't re-open
            try {
              const u = new URL(window.location.href);
              if (u.searchParams.get("new")) {
                u.searchParams.delete("new");
                window.history.replaceState({}, "", u.toString());
              }
            } catch (_) { /* swallow */ }
            await refresh();
          }}
        />
      )}

      {/* Tier-1 external SP stub banner (preserved from iter38) */}
      <div
        className="mb-6 p-3 rounded-md flex items-center justify-between gap-3"
        style={{
          background: "rgba(155,89,182,0.06)",
          border: "1px dashed rgba(155,89,182,0.35)",
        }}
        data-testid="sorting-external-stub"
      >
        <div style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#c89adb", letterSpacing: "0.4px" }}>
          {t("sorting.external_stub")}
        </div>
        <span
          style={{
            background: "rgba(155,89,182,0.18)", color: "#c89adb",
            fontFamily: "DM Mono, monospace", fontSize: 9, padding: "2px 7px",
            borderRadius: 3, letterSpacing: "0.6px",
          }}
        >
          {t("sorting.coming_soon")}
        </span>
      </div>

      {/* SR Picker modal */}
      {pickerOpen && (
        <SRPicker
          srs={openSrsForPicker}
          onPick={openFormForSr}
          onClose={() => setPickerOpen(false)}
          t={t}
        />
      )}

      {/* Inline form */}
      {formFor && (
        <div className="mb-6" data-testid="sorting-inline-form">
          {/* Iter52 — clean point panel sits above the result form */}
          {formFor.sr && (
            <CleanPointPanel
              sr={formFor.sr}
              onUpdated={(updatedSr) => {
                setFormFor((prev) => prev ? { ...prev, sr: updatedSr } : prev);
                refresh();
              }}
            />
          )}
          <SortingResultForm
            srId={formFor.srId}
            incidentId={formFor.incidentId}
            onSubmitted={onSubmitted}
            onCancel={() => setFormFor(null)}
          />
        </div>
      )}

      {/* SECTION 1 — My Sorting Tasks */}
      <Section title={`§1 · ${tt(t, "sorting.tasks_title", "My Sorting Tasks")}`} testId="sorting-tasks-section">
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="sorting-tasks-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className="px-3 py-1.5 rounded transition-colors"
              style={{
                background: filter === f.key ? "rgba(24,95,165,0.16)" : "transparent",
                border: `1px solid ${filter === f.key ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                color: filter === f.key ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
                fontFamily: "DM Mono, monospace",
                fontSize: 11,
                letterSpacing: "0.5px",
                cursor: "pointer",
              }}
              data-testid={`sorting-tasks-filter-${f.key}`}
              aria-pressed={filter === f.key}
            >
              {tt(t, f.labelKey, f.fallback)} <span style={{ opacity: 0.65 }}>({counts[f.key] ?? 0})</span>
            </button>
          ))}
        </div>

        {/* Iter51 — UniversalFilter always visible (even when no tasks yet) so users can see the search box. Empty list shows below. */}
        <UniversalFilter
          storageKey="sorting_tasks"
          items={tasks}
          getSearchText={taskSearchText}
          chips={taskChips}
          placeholder="Search id, title, part #, assignee, incident…"
          testIdPrefix="sorting-tasks-uf"
          emptyMessage="No tasks match the current filter."
        >
          {({ filtered }) => (
            filtered.length === 0 && tasks.length === 0 ? (
              <Empty text={loading ? t("sorting.loading") : tt(t, "sorting.no_tasks", "No sorting tasks in this view.")} testId="sorting-tasks-empty" />
            ) : (
              <div className="rounded-md overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="sorting-tasks-table">
                <TableHeader cols={[
                  tt(t, "sorting.col_sr_id", "SR ID"),
                  tt(t, "sorting.col_incident", "Incident"),
                  tt(t, "sorting.col_requested", "Requested"),
                  tt(t, "sorting.col_due", "Due"),
                  tt(t, "sorting.col_status", "Status"),
                  tt(t, "sorting.col_priority", "Priority"),
                  "",
                ]} widths={[110, "flex-1", 110, 110, 120, 90, 130]} />
                {filtered.map((sr) => (
                  <TaskRow key={sr.id} sr={sr} onSubmit={() => openFormForSr(sr)} canSubmit={canSubmit} t={t} />
                ))}
              </div>
            )
          )}
        </UniversalFilter>
      </Section>

      {/* SECTION 2 — Sorting Results */}
      <Section
        title={`§2 · ${tt(t, "sorting.results_title", "Sorting Results")}`}
        testId="sorting-results-section"
        actions={results.length > 0 ? (
          <>
            <SmallBtn onClick={exportAllXlsx} testId="sorting-export-all-btn">
              📊 Export XLSX
            </SmallBtn>
            <SmallBtn
              onClick={() => setImportSrPickerOpen(true)}
              testId="sorting-import-open-btn"
              color="#9ec3ec"
              bg="rgba(24,95,165,0.10)"
              border="rgba(24,95,165,0.40)"
            >
              📥 Import XLSX
            </SmallBtn>
            <SmallBtn onClick={downloadTemplate} testId="sorting-template-btn" color="#c89adb" bg="rgba(155,89,182,0.10)" border="rgba(155,89,182,0.35)">
              ⬇ Template
            </SmallBtn>
          </>
        ) : null}
      >
        <UniversalFilter
          storageKey="sorting_results"
          items={results}
          getSearchText={resultSearchText}
          chips={resultChips}
          placeholder="Search SORT id, incident, inspector, batch…"
          testIdPrefix="sorting-results-uf"
          emptyMessage="No results match the current filter."
        >
          {({ filtered }) => (
            filtered.length === 0 && results.length === 0 ? (
              <Empty text={loading ? t("sorting.loading") : t("sorting.no_results")} testId="sorting-results-empty" />
            ) : (
              <SortingResultsTable
                results={filtered}
                t={t}
                onOpen={(id) => setDrawerId(id)}
              />
            )
          )}
        </UniversalFilter>
      </Section>

      {/* SECTION 3 — Sorting Analytics */}
      <Section title={`§3 · ${tt(t, "sorting.analytics_title", "Sorting Analytics")}`} testId="sorting-analytics-section">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatCard
            label={tt(t, "sorting.stat_total_parts", "Total parts sorted")}
            value={stats ? stats.total_parts_sorted.toLocaleString() : "—"}
            color="#9ec3ec"
            testId="sorting-stat-total-parts"
          />
          <StatCard
            label={tt(t, "sorting.stat_avg_defect", "Avg defect rate")}
            value={stats ? `${stats.avg_defect_rate_pct.toFixed(1)}%` : "—"}
            color={stats ? rateColor(stats.avg_defect_rate_pct) : "#9aa1b3"}
            testId="sorting-stat-avg-defect"
          />
          <StatCard
            label={tt(t, "sorting.stat_total_events", "Sorting events")}
            value={stats ? String(stats.result_count) : "—"}
            color="#c89adb"
            testId="sorting-stat-total-events"
          />
          <StatCard
            label={tt(t, "sorting.stat_this_month", "Sorting events · this month")}
            value={stats ? String(stats.results_this_month) : "—"}
            color="var(--text, #f0f2f8)"
            testId="sorting-stat-this-month"
          />
        </div>

        {pareto && pareto.items && pareto.items.length > 0 ? (
          <DefectPareto results={(pareto.items || []).map((it) => ({ defect_types: Array(it.count).fill(it.type) }))} testId="sorting-workspace-pareto" />
        ) : (
          <Empty text={tt(t, "sorting.no_defects", "No defect data yet.")} testId="sorting-pareto-empty" />
        )}
      </Section>

      {/* Result detail drawer */}
      {drawerId && (
        <SortingResultDetailDrawer
          resultId={drawerId}
          onClose={() => setDrawerId(null)}
          onRefresh={refresh}
        />
      )}

      {/* Iter40 — Import flow: SR picker → file input → dry-run preview */}
      {importSrPickerOpen && (
        <SRPicker
          srs={openSrsForPicker}
          onPick={(sr) => {
            setImportTargetSr(sr);
            setImportSrPickerOpen(false);
            // Defer file picker open to after state flush
            setTimeout(() => importInputRef.current?.click(), 50);
          }}
          onClose={() => setImportSrPickerOpen(false)}
          t={t}
        />
      )}
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: "none" }}
        data-testid="sorting-import-file-input"
        onChange={async (e) => {
          const file = (e.target.files || [])[0];
          if (!file || !importTargetSr) return;
          setImportBusy(true);
          setImportPreview(null);
          try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(
              `${API}/service-requests/${importTargetSr.id}/sorting-results/import?dry_run=true`,
              fd,
              { headers: { "Content-Type": "multipart/form-data" } },
            );
            setImportPreview({ ...data, _file: file, _sr: importTargetSr });
          } catch (err) {
            const msg = err?.response?.data?.detail || err.message || "Import failed";
            addToast({ type: "error", title: "Import failed", body: typeof msg === "string" ? msg : "Could not parse file" });
          } finally {
            setImportBusy(false);
            if (importInputRef.current) importInputRef.current.value = "";
          }
        }}
      />
      {importPreview && (
        <ImportPreviewModal
          preview={importPreview}
          busy={importBusy}
          onCancel={() => { setImportPreview(null); setImportTargetSr(null); }}
          onConfirm={async () => {
            setImportBusy(true);
            try {
              const fd = new FormData();
              fd.append("file", importPreview._file);
              const { data } = await axios.post(
                `${API}/service-requests/${importPreview._sr.id}/sorting-results/import?dry_run=false`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
              );
              addToast({
                type: "success",
                title: "Sorting results imported",
                body: `${(data.created_ids || []).length} result(s) created`,
              });
              setImportPreview(null);
              setImportTargetSr(null);
              refresh();
            } catch (err) {
              const msg = err?.response?.data?.detail || err.message || "Import failed";
              addToast({ type: "error", title: "Import failed", body: typeof msg === "string" ? msg : "Server error" });
            } finally {
              setImportBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const Header = ({ t, canSubmit, onNewResult, onNewRequest }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
    <div>
      <h1
        style={{
          fontFamily: "'Syne', system-ui, sans-serif",
          fontWeight: 800,
          fontSize: "clamp(26px, 3.4vw, 34px)",
          color: "var(--text, #f0f2f8)",
          letterSpacing: "-0.5px",
          lineHeight: 1.05,
        }}
        data-testid="view-title"
      >
        {t("sorting.page_title")}
      </h1>
      <div className="mt-1.5" style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "var(--text-dim, #c5cad8)" }}>
        {tt(t, "sorting.workspace_subtitle", "Self-contained sorting workspace — tasks, results & analytics")}
      </div>
    </div>
    {canSubmit && (
      <div className="flex gap-2 self-start sm:self-auto flex-wrap">
        <button
          type="button"
          onClick={onNewRequest}
          className="px-4 py-2 rounded-md"
          style={{
            background: "transparent",
            color: "var(--text, #f0f2f8)",
            border: "1px solid rgba(255,255,255,0.18)",
            fontFamily: "Syne, sans-serif",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: "0.4px",
            cursor: "pointer",
          }}
          data-testid="sorting-new-request-btn"
        >
          ＋ {tt(t, "sorting.new_request", "New sorting request")}
        </button>
        <button
          type="button"
          onClick={onNewResult}
          className="px-4 py-2 rounded-md"
          style={{
            background: "#185FA5",
            color: "#fff",
            fontFamily: "Syne, sans-serif",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: "0.4px",
            cursor: "pointer",
          }}
          data-testid="sorting-new-result-btn"
        >
          ＋ {tt(t, "sorting.new_result", "New Sorting Result")}
        </button>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// SR Picker modal
// ---------------------------------------------------------------------------

const SRPicker = ({ srs, onPick, onClose, t }) => (
  <div
    className="fixed inset-0 flex items-center justify-center"
    style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
    onClick={onClose}
    data-testid="sorting-sr-picker-overlay"
  >
    <div
      className="rounded-md p-4"
      style={{ background: "var(--bg2, #0d0f18)", border: "1px solid rgba(255,255,255,0.18)", width: "min(540px, 92vw)", maxHeight: "80vh", overflowY: "auto" }}
      onClick={(e) => e.stopPropagation()}
      data-testid="sorting-sr-picker"
    >
      <div className="flex items-center justify-between mb-3">
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text, #f0f2f8)" }}>
          {tt(t, "sorting.pick_sr_title", "Select sorting request")}
        </span>
        <button onClick={onClose} style={{ color: "#9aa1b3", fontSize: 18, cursor: "pointer", background: "transparent", border: "none" }} data-testid="sorting-sr-picker-close">×</button>
      </div>
      {srs.length === 0 ? (
        <Empty text={tt(t, "sorting.no_open_for_pick", "No open sorting requests available.")} testId="sorting-sr-picker-empty" />
      ) : (
        <div className="space-y-1.5" data-testid="sorting-sr-picker-list">
          {srs.map((sr) => (
            <button
              key={sr.id}
              type="button"
              onClick={() => onPick(sr)}
              className="w-full text-left rounded-md p-2.5"
              style={{
                background: "var(--bg-surface, #111420)",
                border: "1px solid rgba(255,255,255,0.10)",
                cursor: "pointer",
              }}
              data-testid={`sorting-sr-picker-row-${sr.id}`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9ec3ec" }}>{sr.id}</span>
                <StatusBadge status={sr.status} />
              </div>
              <div className="mt-1" style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 13, color: "var(--text, #f0f2f8)" }}>
                {sr.title}
              </div>
              <div className="mt-0.5" style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0" }}>
                {sr.incident_id} · {sr.incident_title || ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const TaskRow = ({ sr, onSubmit, canSubmit, t }) => {
  const created = sr.created_at ? new Date(sr.created_at).toLocaleDateString() : "—";
  const due = sr.due_date ? new Date(sr.due_date).toLocaleDateString() : "—";
  const pri = sr.priority || "medium";
  const isOpen = ["requested", "acknowledged", "in_progress"].includes(sr.status);
  const clickable = isOpen && canSubmit;
  return (
    <Row
      testId={`sorting-tasks-row-${sr.id}`}
      widths={[110, "flex-1", 110, 110, 120, 90, 130]}
      onClick={clickable ? onSubmit : undefined}
      clickable={clickable}
    >
      <Mono color="#9ec3ec">{sr.id}</Mono>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text, #f0f2f8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sr.title}
        </div>
        <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sr.part_number ? <span style={{ color: "#9aebc2" }}>{sr.part_number}</span> : null}
          {sr.part_number && (sr.incident_id || sr.incident_title) ? " · " : null}
          {sr.incident_id ? `${sr.incident_id} ` : null}
          {sr.incident_title || ""}
        </div>
        {/* Iter52 — sorting scope badge */}
        <ScopeBadge sr={sr} />
      </div>
      <Mono>{created}</Mono>
      <Mono>{due}</Mono>
      <StatusBadge status={sr.status} />
      <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: PRIORITY_COLOR[pri] || "#9aa1b3", letterSpacing: "0.5px", textTransform: "uppercase" }}>
        {pri}
      </span>
      {isOpen && canSubmit ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSubmit(); }}
          className="px-2.5 py-1 rounded"
          style={{
            background: "rgba(24,95,165,0.16)",
            border: "1px solid rgba(24,95,165,0.55)",
            color: "#9ec3ec",
            fontFamily: "DM Mono, monospace",
            fontSize: 10,
            letterSpacing: "0.4px",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          data-testid={`sorting-tasks-submit-${sr.id}`}
        >
          {tt(t, "sorting.submit_result", "Submit Result →")}
        </button>
      ) : <span />}
    </Row>
  );
};

// Iter52 — Scope badge rendered inline on each sorting task row.
const ScopeBadge = ({ sr }) => {
  const scope = sr.sorting_scope;
  if (!scope?.type) return null;
  let bg = "rgba(155,89,182,0.12)", fg = "#c89adb", label = "";
  if (scope.type === "quantity") {
    label = `Target ${(scope.target_quantity || 0).toLocaleString()} pcs`;
    bg = "rgba(78,208,132,0.10)"; fg = "#9aebc2";
  } else if (scope.type === "until_date") {
    const when = scope.until_datetime ? new Date(scope.until_datetime) : null;
    label = `Until ${when ? when.toLocaleString() : "—"}`;
    bg = "rgba(243,177,74,0.10)"; fg = "#f3b14a";
  } else if (scope.type === "clean_point") {
    if (!scope.clean_point_dn) {
      label = "Clean point — DN not yet registered";
      bg = "rgba(155,89,182,0.10)"; fg = "#c89adb";
    } else if (!scope.clean_point_plus_one_verified) {
      label = `Clean point ${scope.clean_point_dn} — verifying DN+1`;
      bg = "rgba(243,177,74,0.14)"; fg = "#f3b14a";
    } else {
      label = `Clean point ${scope.clean_point_dn} · DN+1 verified`;
      bg = "rgba(78,208,132,0.10)"; fg = "#9aebc2";
    }
  }
  return (
    <div
      style={{
        marginTop: 3,
        display: "inline-block",
        background: bg,
        color: fg,
        padding: "1px 6px",
        borderRadius: 3,
        fontFamily: "DM Mono, monospace",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
      }}
      data-testid={`sorting-task-scope-${sr.id}`}
    >
      {label}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Iter46 — Results table with horizontal scroll + dynamic defect-type columns
// ---------------------------------------------------------------------------

const _sumDefects = (r) => {
  /** Aggregate defect_breakdown across all entries → flat {type: total}. */
  const out = {};
  const entries = (r.entries && r.entries.length) ? r.entries : [r];
  for (const e of entries) {
    const bd = e.defect_breakdown || {};
    for (const [k, v] of Object.entries(bd)) {
      const n = Number(v) || 0;
      if (!n) continue;
      out[k] = (out[k] || 0) + n;
    }
  }
  return out;
};

const SortingResultsTable = ({ results, t, onOpen }) => {
  // Union of defect types across all visible results — stable lexical order.
  const defectCols = useMemo(() => {
    const set = new Set();
    for (const r of results) {
      const summed = _sumDefects(r);
      for (const k of Object.keys(summed)) set.add(k);
    }
    return Array.from(set).sort();
  }, [results]);

  const cellStyle = {
    fontFamily: "DM Mono, monospace",
    fontSize: 11,
    color: "var(--text, #f0f2f8)",
    padding: "8px 10px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    whiteSpace: "nowrap",
  };
  const headCellStyle = {
    ...cellStyle,
    fontSize: 9,
    letterSpacing: "0.8px",
    color: "var(--dimmer, #9198b0)",
    fontWeight: 700,
    textTransform: "uppercase",
    background: "rgba(255,255,255,0.025)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  return (
    <div
      className="rounded-md sorting-table-scroller"
      style={{
        border: "1px solid rgba(255,255,255,0.10)",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        position: "relative",
      }}
      data-testid="sorting-results-table"
    >
      <table style={{ width: "100%", minWidth: 880, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={headCellStyle}>{tt(t, "sorting.col_sort_id", "SORT ID")}</th>
            <th style={headCellStyle}>{tt(t, "sorting.col_incident", "Incident")}</th>
            <th style={headCellStyle}>{tt(t, "sorting.col_date", "Date")}</th>
            <th style={{ ...headCellStyle, textAlign: "right" }}>Total</th>
            <th style={{ ...headCellStyle, textAlign: "right" }}>OK</th>
            <th style={{ ...headCellStyle, textAlign: "right", color: "#E24B4A" }}>NOK</th>
            <th style={{ ...headCellStyle, textAlign: "right" }}>REW</th>
            <th style={{ ...headCellStyle, textAlign: "right" }}>{tt(t, "sorting.col_defect_rate", "Defect %")}</th>
            {defectCols.map((d) => (
              <th key={d} style={{ ...headCellStyle, textAlign: "right", color: "#E24B4A" }} data-testid={`sorting-results-defect-col-${d}`}>
                {d}
              </th>
            ))}
            <th style={headCellStyle}></th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => {
            const ok = r.total_ok ?? r.ok_qty ?? 0;
            const nok = r.total_nok ?? r.nok_qty ?? 0;
            const rew = r.total_rework ?? r.rework_qty ?? 0;
            const grand = r.grand_total_qty ?? (ok + nok + rew);
            const rate = r.overall_defect_rate_pct ?? r.defect_rate_pct ?? 0;
            const rc = rateColor(rate);
            const summed = _sumDefects(r);
            return (
              <tr
                key={r.id}
                onClick={() => onOpen(r.id)}
                style={{ cursor: "pointer" }}
                data-testid={`sorting-results-row-${r.id}`}
              >
                <td style={{ ...cellStyle, color: "#9ec3ec", fontWeight: 700 }}>{r.id}</td>
                <td style={{ ...cellStyle, minWidth: 180, whiteSpace: "normal" }}>
                  <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 500, fontSize: 12, color: "var(--text, #f0f2f8)" }}>
                    {r.incident_title || "—"}
                  </div>
                  <div style={{ fontSize: 9, color: "var(--dimmer, #9198b0)" }}>
                    {r.incident_id}{r.entries && r.entries.length > 1 ? ` · ${r.entries.length} entries` : ""}
                  </div>
                </td>
                <td style={cellStyle}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                <td style={{ ...cellStyle, textAlign: "right", color: "#9ec3ec", fontWeight: 700 }}>{grand.toLocaleString()}</td>
                <td style={{ ...cellStyle, textAlign: "right", color: "#4ed084" }}>{ok.toLocaleString()}</td>
                <td style={{ ...cellStyle, textAlign: "right", color: "#E24B4A", fontWeight: 700 }}>{nok.toLocaleString()}</td>
                <td style={{ ...cellStyle, textAlign: "right", color: "#f3b14a" }}>{rew.toLocaleString()}</td>
                <td style={{ ...cellStyle, textAlign: "right", color: rc, fontWeight: 700 }}>{rate.toFixed(1)}%</td>
                {defectCols.map((d) => {
                  const v = summed[d] || 0;
                  return (
                    <td
                      key={d}
                      style={{ ...cellStyle, textAlign: "right", color: v ? "#E24B4A" : "var(--dimmer, #9198b0)", fontWeight: v ? 600 : 400 }}
                      data-testid={`sorting-results-defect-cell-${r.id}-${d}`}
                    >{v || ""}</td>
                  );
                })}
                <td style={cellStyle} onClick={(e) => e.stopPropagation()}>
                  <PdfBtn resultId={r.id} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// (Legacy) flex-row helpers retained for §1 Tasks table.
// ---------------------------------------------------------------------------

const ResultRow = ({ r, onOpen }) => {
  const rate = r.overall_defect_rate_pct ?? r.defect_rate_pct ?? 0;
  const rc = rateColor(rate);
  const ok = r.total_ok ?? r.ok_qty ?? 0;
  const nok = r.total_nok ?? r.nok_qty ?? 0;
  const rew = r.total_rework ?? r.rework_qty ?? 0;
  const grand = r.grand_total_qty ?? (ok + nok + rew);
  return (
    <Row testId={`sorting-results-row-${r.id}`} widths={[130, "flex-1", 100, 70, 60, 60, 60, 90, 80]} onClick={onOpen} clickable>
      <Mono color="#9ec3ec">{r.id}</Mono>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 500, fontSize: 12, color: "var(--text, #f0f2f8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {r.incident_title || "—"}
        </div>
        <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0" }}>
          {r.incident_id}{r.entries && r.entries.length > 1 ? ` · ${r.entries.length} entries` : ""}
        </div>
      </div>
      <Mono>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</Mono>
      <Mono color="#9ec3ec" bold>{grand.toLocaleString()}</Mono>
      <Mono color="#4ed084">{ok.toLocaleString()}</Mono>
      <Mono color="#E24B4A">{nok.toLocaleString()}</Mono>
      <Mono color="#f3b14a">{rew.toLocaleString()}</Mono>
      <Mono color={rc} bold>{rate.toFixed(1)}%</Mono>
      <PdfBtn resultId={r.id} />
    </Row>
  );
};

const PdfBtn = ({ resultId }) => {
  const download = async (e) => {
    e.stopPropagation();
    try {
      const resp = await axios.get(`${API}/sorting-results/${resultId}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${resultId}-summary.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { /* swallow */ }
  };
  return (
    <button
      type="button"
      onClick={download}
      className="px-2 py-1 rounded"
      style={{
        background: "rgba(24,95,165,0.12)",
        border: "1px solid rgba(24,95,165,0.40)",
        color: "#9ec3ec",
        fontFamily: "DM Mono, monospace",
        fontSize: 9,
        letterSpacing: "0.4px",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
      data-testid={`sorting-results-pdf-${resultId}`}
    >
      📥 PDF
    </button>
  );
};

// ---------------------------------------------------------------------------
// Export / template helpers
// ---------------------------------------------------------------------------

const exportAllXlsx = async () => {
  try {
    const resp = await axios.get(`${API}/sorting/my-results.xlsx`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a"); a.href = url; a.download = "sorting-results-all.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (_) { /* swallow */ }
};

const downloadTemplate = async () => {
  try {
    const resp = await axios.get(`${API}/sorting-results/import-template.xlsx`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a"); a.href = url; a.download = "sitroom-sorting-import-template.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (_) { /* swallow */ }
};

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

const Section = ({ title, children, testId, actions }) => (
  <div className="mb-8" data-testid={testId}>
    <div className="flex items-end justify-between mb-3">
      <h2 style={{
        fontFamily: "Syne, sans-serif",
        fontWeight: 700,
        fontSize: 16,
        color: "var(--text, #f0f2f8)",
        letterSpacing: "-0.2px",
      }}>{title}</h2>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, color, testId }) => (
  <div
    className="rounded-md p-3"
    style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.12)" }}
    data-testid={testId}
  >
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "1.2px", color: "#9198b0", textTransform: "uppercase" }}>
      {label}
    </div>
    <div className="mt-2" style={{ fontFamily: "DM Mono, monospace", fontSize: 22, color, fontWeight: 500, lineHeight: 1 }}>
      {value}
    </div>
  </div>
);

const TableHeader = ({ cols, widths }) => (
  <div className="flex items-center gap-3 px-3 py-2" style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
    {cols.map((c, i) => (
      <div
        key={i}
        style={{
          flex: widths[i] === "flex-1" ? 1 : `0 0 ${widths[i]}px`,
          width: widths[i] === "flex-1" ? undefined : widths[i],
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
          letterSpacing: "1.2px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        {c}
      </div>
    ))}
  </div>
);

const Row = ({ children, widths, testId, onClick, clickable }) => (
  <div
    className="flex items-center gap-3 px-3 py-2.5"
    style={{
      background: "var(--bg-surface, #0d0f18)",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      cursor: clickable ? "pointer" : "default",
    }}
    onClick={onClick}
    data-testid={testId}
  >
    {React.Children.toArray(children).map((c, i) => (
      <div
        key={i}
        style={{
          flex: widths[i] === "flex-1" ? 1 : `0 0 ${widths[i]}px`,
          width: widths[i] === "flex-1" ? undefined : widths[i],
          minWidth: 0,
        }}
      >
        {c}
      </div>
    ))}
  </div>
);

const Mono = ({ children, color, bold }) => (
  <span style={{
    fontFamily: "DM Mono, monospace",
    fontSize: 11,
    color: color || "var(--text, #f0f2f8)",
    fontWeight: bold ? 700 : 400,
  }}>{children}</span>
);

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE[status] || { bg: "rgba(120,125,140,0.16)", fg: "#9aa1b3" };
  return (
    <span style={{
      background: s.bg, color: s.fg,
      fontFamily: "DM Mono, monospace", fontSize: 9,
      padding: "2px 7px", borderRadius: 3, letterSpacing: "0.5px",
      textTransform: "uppercase",
    }}>{(status || "").replace("_", " ")}</span>
  );
};

const SmallBtn = ({ onClick, children, testId, color = "#9aebc2", bg = "rgba(78,208,132,0.10)", border = "rgba(78,208,132,0.35)" }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-2.5 py-1.5 rounded"
    style={{
      background: bg,
      border: `1px solid ${border}`,
      color,
      fontFamily: "DM Mono, monospace",
      fontSize: 10,
      letterSpacing: "0.4px",
      cursor: "pointer",
    }}
    data-testid={testId}
  >
    {children}
  </button>
);

const Empty = ({ text, testId }) => (
  <div
    className="rounded-md p-6 text-center"
    style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)", fontFamily: "DM Mono, monospace", fontSize: 11, color: "var(--text-dim, #9198b0)" }}
    data-testid={testId}
  >
    {text}
  </div>
);

// Helper: translate with fallback when key is missing
const tt = (t, key, fallback) => {
  const v = t(key);
  return v === key ? fallback : v;
};

// ---------------------------------------------------------------------------
// Import preview modal
// ---------------------------------------------------------------------------

const ImportPreviewModal = ({ preview, busy, onCancel, onConfirm }) => {
  const hasErrors = (preview.errors || []).length > 0;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
      onClick={onCancel}
      data-testid="sorting-import-preview"
    >
      <div
        className="rounded-md p-4"
        style={{
          background: "var(--bg2, #0d0f18)",
          border: `1px solid ${hasErrors ? "rgba(226,75,74,0.40)" : "rgba(155,89,182,0.40)"}`,
          width: "min(620px, 94vw)",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
            Import preview — {preview.would_create} row(s)
          </span>
          <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0" }}>{preview._sr?.id}</span>
        </div>
        {hasErrors && (
          <div
            className="mb-3 p-2 rounded"
            style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)" }}
            data-testid="sorting-import-errors"
          >
            <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#ff9a99", marginBottom: 4 }}>
              {preview.errors.length} error(s) — fix and re-upload
            </div>
            <ul style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#ff9a99", paddingLeft: 16 }}>
              {preview.errors.slice(0, 8).map((e, i) => <li key={i}>· {e}</li>)}
            </ul>
          </div>
        )}
        {(preview.preview || []).length > 0 && (
          <div className="mb-3" style={{ maxHeight: 240, overflowY: "auto", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }} data-testid="sorting-import-rows">
            {preview.preview.slice(0, 20).map((p, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5" style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#c5cad8", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <span>{p.ok_qty} / {p.nok_qty} / {p.rework_qty} · {p.location}</span>
                <span style={{ color: p.defect_rate_pct > 10 ? "#E24B4A" : p.defect_rate_pct > 5 ? "#f3b14a" : "#4ed084" }}>
                  {p.defect_rate_pct}%
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
            data-testid="sorting-import-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || hasErrors}
            className="px-3 py-1.5 rounded"
            style={{
              background: hasErrors ? "#3a3f55" : "#185FA5",
              color: "#fff",
              fontFamily: "Syne, sans-serif",
              fontWeight: 700,
              fontSize: 11,
              cursor: busy ? "wait" : hasErrors ? "not-allowed" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="sorting-import-confirm-btn"
          >
            {busy ? "Importing…" : `Confirm import (${preview.would_create})`}
          </button>
        </div>
      </div>
    </div>
  );
};
